/**
 * useAdminForm Hook
 *
 * Reusable form handling hook for admin components.
 * Eliminates ~500 lines of duplicate code across 10+ components.
 *
 * Features:
 * - Automatic form state management (loading, error, success)
 * - Type-safe form data handling
 * - Built-in error handling with errorMsg utility
 * - Optional validation support
 * - Reset functionality
 * - Checkbox and select input support
 *
 * @example
 * ```typescript
 * const { formData, loading, error, success, handleChange, handleSubmit } = useAdminForm({
 *   initialData: { name: '', email: '' },
 *   onSubmit: async (data) => {
 *     const response = await fetch('/api/update', {
 *       method: 'POST',
 *       body: JSON.stringify(data),
 *     });
 *     if (!response.ok) throw new Error('Failed to save');
 *   },
 * });
 * ```
 */

import { useState, useCallback } from 'react';
import { errorMsg } from '@/lib/utils/error';

export interface UseAdminFormProps<T extends Record<string, any>> {
  /** Initial form data */
  initialData: T;

  /** Submit handler - receives validated form data */
  onSubmit: (data: T) => Promise<void>;

  /** Optional validation function - called before submit */
  validate?: (data: T) => string | null;

  /** Optional success message (default: 'Successfully saved') */
  successMessage?: string;

  /** Optional callback after successful submit */
  onSuccess?: () => void;

  /** Optional callback on error */
  onError?: (error: string) => void;
}

export interface UseAdminFormReturn<T extends Record<string, any>> {
  /** Current form data */
  formData: T;

  /** Loading state during submission */
  loading: boolean;

  /** Error message (null if no error) */
  error: string | null;

  /** Success message (null if no success) */
  success: string | null;

  /** Handle input change events */
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;

  /** Handle checkbox change events */
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;

  /** Handle form submission */
  handleSubmit: (e: React.FormEvent) => Promise<void>;

  /** Manually set form data */
  setFormData: React.Dispatch<React.SetStateAction<T>>;

  /** Reset form to initial state */
  reset: () => void;

  /** Clear error message */
  clearError: () => void;

  /** Clear success message */
  clearSuccess: () => void;

  /** Set error message manually */
  setError: (error: string | null) => void;

  /** Set success message manually */
  setSuccess: (message: string | null) => void;
}

export function useAdminForm<T extends Record<string, any>>({
  initialData,
  onSubmit,
  validate,
  successMessage = 'Successfully saved',
  onSuccess,
  onError,
}: UseAdminFormProps<T>): UseAdminFormReturn<T> {
  const [formData, setFormData] = useState<T>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /**
   * Handle standard input changes (text, textarea, select)
   */
  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error/success on input change
    if (error) setError(null);
    if (success) setSuccess(null);
  }, [error, success]);

  /**
   * Handle checkbox changes
   */
  const handleCheckboxChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));

    // Clear error/success on input change
    if (error) setError(null);
    if (success) setSuccess(null);
  }, [error, success]);

  /**
   * Handle form submission with validation and error handling
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous messages
    setError(null);
    setSuccess(null);

    // Validate if validator provided
    if (validate) {
      const validationError = validate(formData);
      if (validationError) {
        setError(validationError);
        if (onError) onError(validationError);
        return;
      }
    }

    // Submit
    setLoading(true);
    try {
      await onSubmit(formData);
      setSuccess(successMessage);
      if (onSuccess) onSuccess();
    } catch (err) {
      const errorMessage = errorMsg(err);
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [formData, validate, onSubmit, successMessage, onSuccess, onError]);

  /**
   * Reset form to initial state
   */
  const reset = useCallback(() => {
    setFormData(initialData);
    setError(null);
    setSuccess(null);
    setLoading(false);
  }, [initialData]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear success message
   */
  const clearSuccess = useCallback(() => {
    setSuccess(null);
  }, []);

  return {
    formData,
    loading,
    error,
    success,
    handleChange,
    handleCheckboxChange,
    handleSubmit,
    setFormData,
    reset,
    clearError,
    clearSuccess,
    setError,
    setSuccess,
  };
}
